import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useScript } from "../lib/script";

const Ctx = createContext(null);
const GUEST_KEY = "bp-guest-profile";

const readGuest = () => {
  try {
    return JSON.parse(localStorage.getItem(GUEST_KEY) || "null");
  } catch {
    return null;
  }
};

export function ReaderProvider({ children }) {
  const [reader, setReader] = useState(null); // null = checking, false = guest
  const [guest, setGuest] = useState(readGuest); // {profile, stored: true} or session-only
  const [saved, setSaved] = useState(new Set());
  const { script, setScript } = useScript();
  const synced = useRef(null);

  const refresh = useCallback(async () => {
    if (window.location.pathname.startsWith("/admin")) {
      setReader(false);
      return;
    }
    try {
      const { data } = await api.get("/reader/me");
      setReader(data);
      const [{ data: prof }, { data: ids }] = await Promise.all([api.get("/reader/profile"), api.get("/reader/saved/ids")]);
      setSaved(new Set(ids));
      synced.current = prof.script;
      setScript(prof.script || "latn");
    } catch {
      setReader(false);
      setSaved(new Set());
    }
  }, [setScript]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (reader && synced.current !== null && synced.current !== script) {
      synced.current = script;
      api.put("/reader/script", { script }).catch(() => {});
    }
  }, [script, reader]);

  const setGuestProfile = (profile, store) => {
    const g = { profile, stored: !!store, updated_at: new Date().toISOString() };
    if (store) localStorage.setItem(GUEST_KEY, JSON.stringify(g));
    else localStorage.removeItem(GUEST_KEY);
    setGuest(g);
  };
  const resetGuest = () => {
    localStorage.removeItem(GUEST_KEY);
    setGuest(null);
  };
  const logout = async () => {
    try {
      await api.post("/reader/logout");
    } finally {
      setReader(false);
      setSaved(new Set());
    }
  };
  const toggleSave = async (id) => {
    const on = saved.has(id);
    await (on ? api.delete(`/reader/saved/${id}`) : api.post(`/reader/saved/${id}`));
    setSaved((s) => {
      const n = new Set(s);
      on ? n.delete(id) : n.add(id);
      return n;
    });
    return !on;
  };

  return (
    <Ctx.Provider value={{ reader, refresh, setReader, logout, guest, setGuestProfile, resetGuest, saved, toggleSave }}>
      {children}
    </Ctx.Provider>
  );
}

export const useReader = () => useContext(Ctx);
